import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import Visibility from '../../components/global/Visibility';
import PdpRespSimilarItem from './PdpRespSimilarItem/PdpRespSimilarItem';
import QuickViewProvider from '../../global/QuickViewProvider/QuickViewProvider';
import FavoritesProvider from '../../components/global/FavoriteIcons/FavoritesProvider';
import PdpRespSimilarItemsContainer from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';
import { youMayAlsoLike } from '../pdpMessages';

// redux actions
import {
    setSimilarItemsGridElement,
    hasFiredSimilarItemsImpressions,
} from '../../actions/pdpRespSimilarItemsActions';

// helpers
import get from 'lodash.get';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import {
    trackSimilarItemClick,
    trackSimilarItemImpressions,
} from '../tracking/similarItemsTracking';

export class PdpRespSimilarItemsGrid extends React.Component {
    constructor(props) {
        super(props);

        this.onClickItem = this.onClickItem.bind(this);
        this.onContentVisible = this.onContentVisible.bind(this);

        this.state = {
            contentIsVisible: false,
        };
    }
    componentDidMount() {
        // add created node to store so that the can scroll
        // to it from PdpRespSimilarItemsLanding Component
        this.props.setSimilarItemsGridElement(this.getGridEl());
    }
    getGridEl() {
        // making this a function so that it can be mocked in tests
        return ReactDOM.findDOMNode(this);
    }
    onContentVisible() {
        this.setState(
            {
                contentIsVisible: true,
            },
            () => {
                // fire impressions only when items are visible
                if (!this.props.similarItemsState.hasFiredImpressions) {
                    trackSimilarItemImpressions(this.getGridItems());
                    this.props.updateHasFiredImpressions();
                }
            }
        );
    }
    onClickItem(index) {
        trackSimilarItemClick(index, this.getGridItems()[index]);
    }
    getGridItems() {
        return get(this.props, 'similarItems.edges') || [];
    }
    render() {
        const gridItems = this.getGridItems();

        if (!gridItems.length) {
            return null;
        }

        const { contentIsVisible } = this.state;
        const { isDark, viewer, currency, user, hasFetchedOnClient } = this.props;
        const { isClient, fetchFavorites } = this.props.relayVariables;
        const favoritesProviderProps = {
            location: locations.SIMILAR_ITEM_GRID,
            disable: !isClient || !contentIsVisible || !hasFetchedOnClient,
            userId: user && user.serviceId ? user.serviceId : null,
            environment: this.props.relay.environment,
            itemIds: gridItems.map(({ node }) => node.serviceId),
            fetchFavorites,
            viewer: viewer || null,
        };

        return (
            <PdpRespSimilarItemsContainer
                isDark={isDark}
                hideBar={isDark}
                header={youMayAlsoLike}
                dataTn="pdp-similar-recommendation-grid"
            >
                <Visibility onContentVisible={this.onContentVisible} />
                <FavoritesProvider {...favoritesProviderProps}>
                    {/* favorites  - passed to PdpRespFavoritingItem in PdpRespSimilarItem */}
                    {({ favorites }) => (
                        <QuickViewProvider>
                            {/* quickView - passed to PdpRespSimilarItemQuickView in PdpRespSimilarItem */}
                            {({ quickView }) => {
                                const itemProps = {
                                    isClient,
                                    quickView,
                                    favorites,
                                    onClick: this.onClickItem,
                                    user,
                                    showLightBox: true,
                                };
                                return (
                                    <div className="rowFlex">
                                        {gridItems.map(({ node }, index) => (
                                            <div
                                                key={node.serviceId}
                                                className="colLg3 colMd6 colXs6"
                                            >
                                                {/* Grid of "You May Also Like" items */}
                                                <PdpRespSimilarItem
                                                    item={node}
                                                    index={index}
                                                    viewer={viewer}
                                                    currency={currency}
                                                    {...itemProps}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                );
                            }}
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            </PdpRespSimilarItemsContainer>
        );
    }
}

PdpRespSimilarItemsGrid.defaultProps = {
    isDark: true,
};

PdpRespSimilarItemsGrid.propTypes = {
    hasFetchedOnClient: PropTypes.bool,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        showSimilarItems: PropTypes.bool,
        isClient: PropTypes.bool.isRequired,
        fetchFavorites: PropTypes.bool.isRequired,
    }).isRequired,
    item: PropTypes.object.isRequired,
    isDark: PropTypes.bool,
    similarItems: PropTypes.object,
    relay: PropTypes.shape({
        environment: PropTypes.object.isRequired,
    }).isRequired,
    currency: PropTypes.string,
    user: PropTypes.object,

    // redux
    similarItemsState: PropTypes.shape({
        hasFiredImpressions: PropTypes.bool.isRequired,
    }).isRequired,
    updateHasFiredImpressions: PropTypes.func.isRequired,
    setSimilarItemsGridElement: PropTypes.func.isRequired,
    viewer: PropTypes.object,
};

// Redux Map Functions
function mapStateToProps({ similarItems }) {
    return {
        similarItemsState: {
            hasFiredImpressions: similarItems.ecommerceImpressions.hasFiredSimilarItems,
        },
    };
}
function mapDispatchToProps(dispatch) {
    return {
        updateHasFiredImpressions: () => dispatch(hasFiredSimilarItemsImpressions()),
        setSimilarItemsGridElement: gridEl => dispatch(setSimilarItemsGridElement(gridEl)),
    };
}

export default createFragmentContainer(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(PdpRespSimilarItemsGrid),
    {
        viewer: graphql`
            fragment PdpRespSimilarItemsGrid_viewer on Viewer {
                ...FavoritesProvider_viewer
                ...PdpRespSimilarItem_viewer
            }
        `,
        similarItems: graphql`
            fragment PdpRespSimilarItemsGrid_similarItems on moreLikeThisConnection {
                edges {
                    node {
                        serviceId
                        categoryPath
                        ecommerceTrackingParams(userId: $userId) @include(if: $isClient)
                        ...PdpRespSimilarItem_item
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpRespSimilarItemsGrid_user on User {
                serviceId
                ...PdpRespSimilarItem_user
            }
        `,
    }
);
